import { worldToLocal } from "@/modes/alignment-modes-commons/alignment-transform";
import { GUID } from "@faro-lotv/foundation";
import { isValid } from "@faro-lotv/ielement-types";
import { memberWithPrivateData } from "@faro-lotv/lotv";
import {
  CaptureTreeEntityRevision,
  RegistrationEdgeRevision,
} from "@faro-lotv/service-wires";
import { Matrix4, Quaternion, Vector3 } from "three";
import { ScanConnection } from "../utils/edge-utils";
import { RevisionTransformCache } from "./revision/revision-transform-cache";
import { getEntityTransformCache } from "./revision/selector-helpers";

/** Tolerance for distances to account for floating point errors. */
const DISTANCE_EPSILON = 0.001;
/** The angles can quickly accumulate errors and need lower precision. */
const ANGLE_EPSILON = 0.1;

/**
 * Check if the transform stored in the edge matches the relative transform between the connected scans.
 *
 * @param transformCache The cache for the transforms of the revision entities.
 * @param edge The edge to check.
 * @returns `true` if the edge transform is valid, `false` otherwise.
 */
export const isEdgeTransformValid = memberWithPrivateData(() => {
  const sourceTransform = new Matrix4();
  const targetTransform = new Matrix4();

  const edgePos = new Vector3();
  const edgeRot = new Quaternion();

  const relativePos = new Vector3();
  const relativeRot = new Quaternion();

  const ignoreScale = new Vector3();

  return (
    transformCache: RevisionTransformCache,
    edge: RegistrationEdgeRevision,
  ): boolean => {
    const edgeTransform = edge.data?.transformation;
    if (!edgeTransform) {
      // If the edge has no transform, it cannot be invalidated
      return true;
    }

    edgePos.set(edgeTransform.pos.x, edgeTransform.pos.y, edgeTransform.pos.z);
    edgeRot.set(
      edgeTransform.rot.x,
      edgeTransform.rot.y,
      edgeTransform.rot.z,
      edgeTransform.rot.w,
    );

    const sourceCache = getEntityTransformCache(transformCache, edge.sourceId);
    const targetCache = getEntityTransformCache(transformCache, edge.targetId);

    sourceTransform.fromArray(sourceCache.worldMatrix);
    targetTransform.fromArray(targetCache.worldMatrix);

    // Check that the transform saved on the edge still matches the actual relative transform
    const relativeTransform = worldToLocal(targetTransform, sourceTransform);
    relativeTransform.decompose(relativePos, relativeRot, ignoreScale);

    // Compare transform in first direction
    if (
      relativePos.distanceTo(edgePos) < DISTANCE_EPSILON &&
      relativeRot.angleTo(edgeRot) < ANGLE_EPSILON
    ) {
      return true;
    }

    // Older client versions represented the edge transform in the inverse direction
    // This would cause no edges to be displayed, so the other direction also has to be checked
    const relativeTransformInv = worldToLocal(sourceTransform, targetTransform);
    relativeTransformInv.decompose(relativePos, relativeRot, ignoreScale);

    // Compare transform in the inverse direction
    return (
      relativePos.distanceTo(edgePos) < DISTANCE_EPSILON &&
      relativeRot.angleTo(edgeRot) < ANGLE_EPSILON
    );
  };
});

/**
 * Finds all connected components in a graph given entities and edges.
 *
 * @param scanEntities Array of scan entities to group.
 * @param edges Array of edges connecting entities.
 * @param atomicClusterIds The IDs of all clusters that should be considered an atomic unit (i.e. all children are connected).
 * @returns Array of groups, where each group is an array of entity IDs that are connected to each other.
 */
export function findConnectedComponents(
  scanEntities: CaptureTreeEntityRevision[],
  edges: ScanConnection[],
  atomicClusterIds: GUID[],
): GUID[][] {
  const adjacencyMap = new Map<GUID, Set<GUID>>();

  scanEntities.forEach((entity) => {
    adjacencyMap.set(entity.id, new Set());
  });

  edges.forEach(({ sourceId, targetId }) => {
    addAdjacency(adjacencyMap, sourceId, targetId);
  });

  // add the scans in the clusters to the adjacency map
  buildAtomicClusterAdjacencyMap(adjacencyMap, atomicClusterIds, scanEntities);

  const visited = new Set<GUID>();
  const groups: GUID[][] = [];

  // Helper function for depth
  function findConnectedEntities(startId: GUID, group: GUID[]): void {
    if (visited.has(startId)) return;
    visited.add(startId);
    group.push(startId);
    const neighbors = adjacencyMap.get(startId) ?? new Set();
    neighbors.forEach((nextId) => findConnectedEntities(nextId, group));
  }

  scanEntities.forEach((entity) => {
    if (!visited.has(entity.id)) {
      const group: GUID[] = [];
      findConnectedEntities(entity.id, group);
      groups.push(group);
    }
  });

  return groups;
}

/**
 * Groups entities by connected components.
 *
 * @param connectedGroups Array of arrays of entity IDs (connected components).
 * @param entities Array of all entities.
 * @returns Array of arrays of entities grouped by connected components.
 */
export function getConnectedEntitiesGroups(
  connectedGroups: GUID[][],
  entities: CaptureTreeEntityRevision[],
): CaptureTreeEntityRevision[][] {
  if (!Array.isArray(connectedGroups)) return [];
  return connectedGroups.map((group) =>
    group
      .map((componentId) =>
        entities.find((entity) => entity.id === componentId),
      )
      .filter(isValid),
  );
}

/**
 * @param adjacencyMap The adjacency map to edit; to add atomic cluster relations.
 * @param atomicClusterIds The IDs of the clusters which should be considered an atomic unit in the adjacency map.
 * @param scanEntities The scans to consider for the adjacency map.
 */
function buildAtomicClusterAdjacencyMap(
  adjacencyMap: Map<GUID, Set<GUID>>,
  atomicClusterIds: GUID[],
  scanEntities: CaptureTreeEntityRevision[],
): void {
  const atomicGroups: Map<GUID, GUID[]> = new Map();

  // Determine which scans belong to which atomic cluster
  for (const scan of scanEntities) {
    const { id, parentId } = scan;

    // ASSUMPTION: Atomic clusters are flat, i.e. only have direct children
    if (parentId && atomicClusterIds.includes(parentId)) {
      const group = atomicGroups.get(parentId) ?? [];
      group.push(id);
      atomicGroups.set(parentId, group);
    }
  }

  // Add adjacency relations for atomic clusters
  for (const group of atomicGroups.values()) {
    if (group.length <= 1) continue;

    // For efficiency, make the first member in the group adjacent to all others
    // Due to transitive checks, they will all count as connected
    const [anchorId, ...otherIds] = group;

    for (const otherId of otherIds) {
      addAdjacency(adjacencyMap, anchorId, otherId);
    }
  }
}

/**
 * @param adjacencyMap The adjacency map to add the adjacent scans to.
 * @param scan1Id The ID of the first adjacent scan.
 * @param scan2Id The ID of the second adjacent scan.
 */
function addAdjacency(
  adjacencyMap: Map<GUID, Set<GUID>>,
  scan1Id: GUID,
  scan2Id: GUID,
): void {
  // ASSUMPTION: For each scan, the set has already been initialized

  // Bidirectional relation
  adjacencyMap.get(scan1Id)?.add(scan2Id);
  adjacencyMap.get(scan2Id)?.add(scan1Id);
}
